import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useAuth } from '../../../hooks/useAuth';
import { GooeyLoader } from '../../../components/ui/gooey-loader';
import { resolveApiBaseUrl } from '../../../utils/apiClient';
import { latnLocale } from '../../../utils/numberFormat';

const API_BASE_URL = resolveApiBaseUrl();

type Complaint = {
  id: string;
  category: string;
  status: string;
  priority: string;
  subject: string;
  description: string;
  createdAt: string;
  resolvedAt?: string;
  satisfactionRating?: number;
  property?: { id: string; title: string; slug: string };
  agent?: { id: string; displayName: string };
  company?: { id: string; name: string };
  _count?: { updates: number; attachments: number };
};

const categoryLabels: Record<string, { en: string; ar: string; icon: string }> = {
  SERVICE_QUALITY: { en: 'Service Quality', ar: 'جودة الخدمة', icon: 'ri-customer-service-2-line' },
  AGENT_BEHAVIOR: { en: 'Agent Behavior', ar: 'سلوك الوكيل', icon: 'ri-user-star-line' },
  PROPERTY_ISSUES: { en: 'Property Issues', ar: 'مشاكل العقار', icon: 'ri-home-gear-line' },
  PAYMENT_BILLING: { en: 'Payment & Billing', ar: 'الدفع والفواتير', icon: 'ri-bank-card-line' },
  TECHNICAL_ISSUES: { en: 'Technical Issues', ar: 'مشاكل تقنية', icon: 'ri-bug-line' },
  OTHER: { en: 'Other', ar: 'أخرى', icon: 'ri-question-line' },
};

const statusConfig: Record<string, { en: string; ar: string; color: string; bgColor: string }> = {
  NEW: { en: 'New', ar: 'جديد', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  UNDER_REVIEW: { en: 'Under Review', ar: 'قيد المراجعة', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  IN_PROGRESS: { en: 'In Progress', ar: 'قيد المعالجة', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  RESOLVED: { en: 'Resolved', ar: 'تم الحل', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  CLOSED: { en: 'Closed', ar: 'مغلق', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  REJECTED: { en: 'Rejected', ar: 'مرفوض', color: 'text-rose-700', bgColor: 'bg-rose-100' },
};

const priorityConfig: Record<string, { en: string; ar: string; color: string }> = {
  LOW: { en: 'Low', ar: 'منخفض', color: 'text-gray-600' },
  MEDIUM: { en: 'Medium', ar: 'متوسط', color: 'text-blue-600' },
  HIGH: { en: 'High', ar: 'عالي', color: 'text-amber-600' },
  URGENT: { en: 'Urgent', ar: 'عاجل', color: 'text-rose-600' },
};

export default function MyComplaintsPage() {
  const { i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const isRTL = i18n.dir() === 'rtl';

  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 0 });

  useEffect(() => {
    if (!user) {
      navigate('/signin?redirect=/complaints/my');
      return;
    }
    fetchComplaints();
  }, [user, statusFilter, pagination.page]);

  const fetchComplaints = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        pageSize: pagination.pageSize.toString(),
      });
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${API_BASE_URL}/complaints/my?${params}`, {
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setComplaints(data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data.meta?.total || 0,
          totalPages: data.meta?.totalPages || 0,
        }));
      } else {
        setError(data.message || 'Failed to load complaints');
      }
    } catch (err) {
      console.error('Error fetching complaints:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-US'), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!user) {
    return null;
  }

  return (
    <div
      className="min-h-screen bg-white"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#004D40] py-12 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold md:text-3xl">
                {isRTL ? 'شكواي' : 'My Complaints'}
              </h1>
              <p className="mt-1 text-[#DDEEEB]">
                {isRTL ? 'تتبع حالة شكواك' : 'Track the status of your complaints'}
              </p>
            </div>
            <Button
              onClick={() => navigate('/complaints')}
              className="bg-white text-[#004D40] hover:bg-[var(--pv-color-surface-sunken)]"
            >
              <i className="ri-add-line mr-2"></i>
              {isRTL ? 'تقديم شكوى جديدة' : 'Submit New Complaint'}
            </Button>
          </div>
        </div>
      </section>

      <main className="py-8">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-slate-700">
              {isRTL ? 'تصفية حسب الحالة:' : 'Filter by status:'}
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setStatusFilter('');
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                  !statusFilter
                    ? 'bg-[#004D40] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {isRTL ? 'الكل' : 'All'}
              </button>
              {Object.entries(statusConfig).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => {
                    setStatusFilter(key);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                    statusFilter === key
                      ? `${config.bgColor} ${config.color}`
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {isRTL ? config.ar : config.en}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
              <i className="ri-error-warning-line text-4xl text-rose-500"></i>
              <p className="mt-2 text-rose-700">{error}</p>
              <Button onClick={fetchComplaints} className="mt-4" variant="secondary">
                {isRTL ? 'إعادة المحاولة' : 'Try Again'}
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && complaints.length === 0 && (
            <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)]">
                <i className="ri-file-list-3-line text-4xl text-[#004D40]"></i>
              </div>
              <h3 className="mb-2 text-xl font-bold text-slate-900">
                {isRTL ? 'لا توجد شكاوى' : 'No Complaints Yet'}
              </h3>
              <p className="mb-6 text-slate-500">
                {statusFilter
                  ? isRTL
                    ? 'لا توجد شكاوى بهذه الحالة'
                    : 'No complaints with this status'
                  : isRTL
                  ? 'لم تقم بتقديم أي شكوى بعد'
                  : "You haven't submitted any complaints yet"}
              </p>
              <Button onClick={() => navigate('/complaints')}>
                <i className="ri-add-line mr-2"></i>
                {isRTL ? 'تقديم شكوى جديدة' : 'Submit a Complaint'}
              </Button>
            </div>
          )}

          {/* Complaints List */}
          {!loading && !error && complaints.length > 0 && (
            <div className="space-y-4">
              {complaints.map((complaint) => {
                const category = categoryLabels[complaint.category] || categoryLabels.OTHER;
                const status = statusConfig[complaint.status] || statusConfig.NEW;
                const priority = priorityConfig[complaint.priority] || priorityConfig.MEDIUM;

                return (
                  <Link
                    key={complaint.id}
                    to={`/complaints/my/${complaint.id}`}
                    className="block rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:border-[#1B7566] hover:shadow-md"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${status.bgColor} ${status.color}`}
                          >
                            {isRTL ? status.ar : status.en}
                          </span>
                          <span className={`text-xs font-medium ${priority.color}`}>
                            <i className="ri-flag-line mr-1"></i>
                            {isRTL ? priority.ar : priority.en}
                          </span>
                          <span className="text-xs text-slate-400">
                            #{complaint.id.slice(-8).toUpperCase()}
                          </span>
                        </div>

                        <h3 className="mt-2 text-lg font-semibold text-slate-900">
                          {complaint.subject}
                        </h3>

                        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <i className={`${category.icon}`}></i>
                            {isRTL ? category.ar : category.en}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {formatDate(complaint.createdAt)}
                          </span>
                          {complaint._count?.updates && complaint._count.updates > 0 && (
                            <span className="flex items-center gap-1">
                              <i className="ri-chat-3-line"></i>
                              {complaint._count.updates}{' '}
                              {isRTL ? 'تحديثات' : 'updates'}
                            </span>
                          )}
                        </div>

                        {/* Linked entities */}
                        {(complaint.property || complaint.agent || complaint.company) && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {complaint.property && (
                              <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">
                                <i className="ri-home-line"></i>
                                {complaint.property.title}
                              </span>
                            )}
                            {complaint.agent && (
                              <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">
                                <i className="ri-user-line"></i>
                                {complaint.agent.displayName}
                              </span>
                            )}
                            {complaint.company && (
                              <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">
                                <i className="ri-building-line"></i>
                                {complaint.company.name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {complaint.satisfactionRating && (
                          <div className="flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <i
                                key={star}
                                className={`ri-star-fill text-sm ${
                                  star <= complaint.satisfactionRating!
                                    ? 'text-amber-400'
                                    : 'text-gray-300'
                                }`}
                              ></i>
                            ))}
                          </div>
                        )}
                        <i className="ri-arrow-right-s-line text-xl text-slate-400"></i>
                      </div>
                    </div>
                  </Link>
                );
              })}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-6">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <span className="text-sm text-slate-600">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
